import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, Loader2, Pencil } from "lucide-react";
import { useTeamRecordings } from "@/hooks/useTeams";
import type { Team, TeamRecordingSummary } from "@/types";
import { formatUserName } from "@/utils/userName";
import RecordingsFilterBar from "./RecordingsFilterBar";
import RecordingEditModal from "./RecordingEditModal";

type TeamMembers = NonNullable<Team["members"]>;

interface RecordingsTabProps {
  teamId: string;
  canManage: boolean;
  members: TeamMembers;
}

function distinctById<T extends { id: string; label: string }>(items: T[]): T[] {
  return [...new Map(items.map((item) => [item.id, item])).values()];
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

export default function RecordingsTab({
  teamId,
  canManage,
  members,
}: RecordingsTabProps) {
  const { t, i18n } = useTranslation("pages");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [editing, setEditing] = useState<TeamRecordingSummary | null>(null);

  const filters = useMemo(() => {
    const from = searchParams.get("from");
    const to = searchParams.get("to");
    return {
      eventId: searchParams.get("eventId") ?? undefined,
      sessionId: searchParams.get("sessionId") ?? undefined,
      driverId: searchParams.get("driverId") ?? undefined,
      from: from ? `${from}T00:00:00.000Z` : undefined,
      to: to ? `${to}T23:59:59.999Z` : undefined,
      limit: 25,
      offset: Number(searchParams.get("offset") ?? 0),
    };
  }, [searchParams]);

  const { data, isLoading, isError } = useTeamRecordings(teamId, filters);
  const recordings = data?.recordings ?? [];

  const eventOptions = distinctById(
    recordings.map((r) => ({ id: r.session.event.id, label: r.session.event.name })),
  );
  const sessionOptions = distinctById(
    recordings.map((r) => ({ id: r.session.id, label: r.session.title })),
  );
  const driverOptions = members.map((m) => ({
    id: m.userId,
    label: formatUserName(m.user),
  }));

  function setSearchParamsWithOffset(offset: number) {
    const params = new URLSearchParams(searchParams);
    params.set("offset", String(offset));
    navigate(`?${params.toString()}`, { replace: true });
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[30vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center text-sm text-red-600">
        <AlertCircle className="mr-2 h-4 w-4" aria-hidden="true" />
        {t("teams.recordings.loadFailed")}
      </div>
    );
  }

  return (
    <div>
      <RecordingsFilterBar
        events={eventOptions}
        sessions={sessionOptions}
        drivers={driverOptions}
      />

      {recordings.length === 0 ? (
        <p className="text-sm text-secondary-500">
          {t("teams.recordings.empty")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-secondary-200 text-left text-secondary-500">
                <th className="py-2 pr-3">{t("teams.recordings.recordedAt")}</th>
                <th className="py-2 pr-3">{t("teams.recordings.angle")}</th>
                <th className="py-2 pr-3">{t("teams.recordings.duration")}</th>
                <th className="py-2 pr-3">{t("teams.recordings.event")}</th>
                <th className="py-2 pr-3">{t("teams.recordings.track")}</th>
                <th className="py-2 pr-3">{t("teams.recordings.car")}</th>
                <th className="py-2 pr-3">{t("teams.recordings.driver")}</th>
                <th className="py-2 pr-3">{t("teams.recordings.session")}</th>
                {canManage && <th className="py-2 pr-3" />}
              </tr>
            </thead>
            <tbody>
              {recordings.map((recording) => (
                <tr key={recording.id} className="border-b border-secondary-100">
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {recording.recordedAt
                      ? new Date(recording.recordedAt).toLocaleDateString(
                          i18n.language,
                        )
                      : "—"}
                  </td>
                  <td className="py-2 pr-3">{recording.angleName ?? "—"}</td>
                  <td className="py-2 pr-3">
                    {formatDuration(recording.duration)}
                  </td>
                  <td className="py-2 pr-3">{recording.session.event.name}</td>
                  <td className="py-2 pr-3">{recording.session.track.name}</td>
                  <td className="py-2 pr-3">{recording.session.vehicle.name}</td>
                  <td className="py-2 pr-3">
                    {formatUserName(recording.session.driver)}
                  </td>
                  <td className="py-2 pr-3">
                    <Link
                      to={`/sessions/${recording.session.id}`}
                      className="text-primary-600 hover:underline"
                    >
                      {recording.session.title}
                    </Link>
                  </td>
                  {canManage && (
                    <td className="py-2 pr-3 text-right">
                      <button
                        type="button"
                        aria-label={t("teams.recordings.edit")}
                        className="inline-flex items-center gap-1 rounded-md border border-secondary-300 px-2 py-1 text-xs hover:bg-secondary-100"
                        onClick={() => setEditing(recording)}
                      >
                        <Pencil className="h-3 w-3" aria-hidden="true" />
                        {t("teams.recordings.edit")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.meta.total > data.meta.limit && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <button
            type="button"
            className="rounded-md border border-secondary-300 px-2 py-1 disabled:opacity-40"
            disabled={data.meta.offset === 0}
            onClick={() => {
              setSearchParamsWithOffset(data.meta.offset - data.meta.limit);
            }}
          >
            {t("teams.recordings.prev")}
          </button>
          <span className="text-secondary-600">
            {data.meta.offset + 1}–
            {Math.min(data.meta.offset + data.meta.limit, data.meta.total)} /{" "}
            {data.meta.total}
          </span>
          <button
            type="button"
            className="rounded-md border border-secondary-300 px-2 py-1 disabled:opacity-40"
            disabled={data.meta.offset + data.meta.limit >= data.meta.total}
            onClick={() => {
              setSearchParamsWithOffset(data.meta.offset + data.meta.limit);
            }}
          >
            {t("teams.recordings.next")}
          </button>
        </div>
      )}

      {editing && canManage && (
        <RecordingEditModal
          teamId={teamId}
          recording={editing}
          members={members}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
